import { Component } from 'react';
import { logger } from '@helpers/logger';
import PropTypes from 'prop-types';
import './ErrorBoundary.scss';

class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
            isOnline: navigator.onLine
        };
    }

    componentDidMount() {
        window.addEventListener('online', this.handleOnlineStatus);
        window.addEventListener('offline', this.handleOnlineStatus);
    }

    componentWillUnmount() {
        window.removeEventListener('online', this.handleOnlineStatus);
        window.removeEventListener('offline', this.handleOnlineStatus);
    }

    handleOnlineStatus = () => {
        const isOnline = navigator.onLine;
        this.setState({ isOnline });

        if (isOnline && this.state.hasError) {
            window.location.reload();
        }
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        logger.error('Unhandled error in component:', error, errorInfo);
        this.setState({ errorInfo });
    }

    render() {
        const { hasError, error, errorInfo, isOnline } = this.state;

        if (hasError && !isOnline) {
            return (
                <div className="error-boundary offline">
                    <div className="icon-offline"></div>
                    <h1>You&apos;re offline</h1>
                    <p>Please check your internet connection and try again.</p>
                </div>
            );
        }

        if (hasError) {
            return (
                <div className="error-boundary">
                    <h1>Something went wrong</h1>
                    <p>We&apos;re sorry, but an unexpected error occurred.</p>
                    {error && <pre style={{ cursor: 'text', userSelect: 'text' }}>{error.toString()}</pre>}
                    {errorInfo && <details><summary>Details</summary><pre>{errorInfo.componentStack}</pre></details>}
                </div>
            );
        }

        return this.props.children;
    }
}

ErrorBoundary.propTypes = {
    children: PropTypes.node.isRequired,
};

export default ErrorBoundary;
